// import 'virtual:objectiv-developer-tools';
import { useAB, useProspect } from '@insify/funnel-components';
import {
  InsifyObjectivProvider,
  getDeviceIdentityContext,
  getDistributionIdentityContext,
  getFlagSetIdentityContext,
  getProspectIdentityContext,
  getRelationIdentityContext,
  importDevtoolsDynamically,
} from '@insify/objectiv';
import { ensureDeviceId } from '@insify/ui-utils';
import { IdentityContextPlugin } from '@objectiv/plugin-identity-context';
import { LocaleContextPlugin } from '@objectiv/plugin-locale-context';
import { RootLocationContextFromURLPlugin } from '@objectiv/plugin-root-location-context-from-url';
import { makeId } from '@objectiv/tracker-core';
import { useTranslation } from 'react-i18next';

import { env, isLocalDev, isProd } from '../../util/env';

import type { TrackerConfig } from '@objectiv/tracker-core';

const applicationId = `${env}-funnel-fr`;

// Importing virtual:objectiv-developer-tools works on build time while importDevtoolsDynamically works on runtime.
// Importing the dev tools on runtime misses some logs during the initialization which is fine in normal usage.
// If you need to debug the initialization process, you can uncomment the "import 'virtual:objectiv-developer-tools';" on top and comment out the following lines to avoid a duplicated import.
if (window.insify_runtime_config.OBJECTIV_DEVTOOLS === 'true') {
  importDevtoolsDynamically();
}

export const ObjectivTrackerProvider: React.FC<{ children: React.ReactNode; isConsentGiven: boolean }> = ({
  children,
  isConsentGiven,
}) => {
  const prospectId = useProspect();
  const deviceId = ensureDeviceId(isLocalDev ? undefined : '.insify.fr');
  const { i18n } = useTranslation();
  const ab = useAB();

  const plugins: TrackerConfig['plugins'] = [
    new LocaleContextPlugin({
      idFactoryFunction: () => i18n.language,
    }),
    new RootLocationContextFromURLPlugin({
      idFactoryFunction: () => {
        const lastSlug = location.pathname.split('/').pop();
        return lastSlug ? makeId(lastSlug) : 'funnel';
      },
    }),
    new IdentityContextPlugin([
      ...getProspectIdentityContext(prospectId),
      ...getDeviceIdentityContext(deviceId),
      ...getRelationIdentityContext(ab.relation_id as string),
      ...getDistributionIdentityContext(),
      ...getFlagSetIdentityContext('frPnc', ab.experiments),
    ]),
  ];

  return (
    <InsifyObjectivProvider
      applicationId={applicationId}
      isProd={isProd}
      active={true}
      isConsentGiven={isConsentGiven}
      plugins={plugins}
      isLogEnabled={window.insify_runtime_config.OBJECTIV_DEVTOOLS === 'true'}
    >
      {children}
    </InsifyObjectivProvider>
  );
};
